import { useMemo, useState } from 'react';
import { sortHand, whistPartnerOf, whistTeamOf, type WhistLegal, type WhistView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' } as const;

export function WhistTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as WhistView;
  const legal = useGame((s) => s.legal) as WhistLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? whistTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const teamTricks = (team: number) => (view.tricksWon[team] ?? 0) + (view.tricksWon[team + 2] ?? 0);

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[whistTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === whistPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                <span className="tag">{view.tricksWon[s] ?? 0}</span>
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('wh.hand', { n: view.round })} · {t('wh.trump')}{' '}
            <b style={{ color: view.trump === 'H' || view.trump === 'D' ? 'var(--danger)' : undefined }}>{SUIT_SYMBOL[view.trump]}</b>
            {view.trumpCardInHand && ` · ${t('wh.dealerHolds', { name: name(view.dealer) })}`}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
            <span className="pill team-pill mine">{t('sp.us')}: {view.teamScores[myTeam] ?? 0} · {t('wh.tricks', { n: teamTricks(myTeam) })}</span>
            <span className="pill team-pill">{t('sp.them')}: {view.teamScores[1 - myTeam] ?? 0} · {t('wh.tricks', { n: teamTricks(1 - myTeam) })}</span>
          </div>
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'HAND_END' && view.handResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('wh.handScores', { n: view.round })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.handResults.at(-1)!.map((r, team) => (
                  <span key={team} className={`pill ${r.points > 0 ? 'accent' : ''}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: {r.tricks} → +{r.points}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat
          seat={mySeat !== null ? seats[mySeat] : undefined}
          sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts · {t('wh.tricks', { n: view.tricksWon[mySeat] ?? 0 })}</> : null}
        />
      </div>
      <div className="table-bottom">
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={view.tricks.length === 0 && view.trick.length === 0 ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.handResults.map((row, r) => (
              <tr key={r}>
                <td>H{r + 1}</td>
                {row.map((x, team) => (
                  <td key={team} className="num">
                    {x.points > 0 ? `+${x.points}` : '0'}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('wh.tricks', { n: x.tricks })}</div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('wh.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
